import React, { useEffect, useRef, useState } from "react";

/**
 * NavDropdown.
 *
 * Hover / focus-activated dropdown for desktop nav. No chevron — the
 * dropdown trigger sits on the same horizontal baseline as the
 * non-dropdown links so the row reads as a single typographic line.
 * Reveal is a soft cinematic fade + lift (no rotation, no spinning
 * indicator) so the panel feels like a quiet curtain reveal rather
 * than a UI control. Closes on outside click, escape, or selection.
 */
const NavDropdown = ({ link, onLinkClick }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const closeTimer = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const openMenu = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 140);
  };

  return (
    <div
      ref={wrapRef}
      onMouseEnter={openMenu}
      onMouseLeave={scheduleClose}
      className="relative"
    >
      <button
        type="button"
        data-testid={`nav-dropdown-${link.id}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onFocus={openMenu}
        className={`nav-link nav-dropdown-trigger text-[10.5px] xl:text-[11px] tracking-[0.18em] 2xl:tracking-[0.22em] uppercase ${open ? "is-open" : ""}`}
      >
        {link.label}
      </button>
      <div
        role="menu"
        data-testid={`nav-dropdown-panel-${link.id}`}
        aria-hidden={!open}
        className={`nav-dropdown-reveal absolute top-full left-1/2 -translate-x-1/2 mt-3 min-w-[220px] py-2 nav-dropdown-panel border border-gold/25 backdrop-blur-xl z-50 ${open ? "is-open" : ""}`}
      >
        {link.items.map((item) => {
          const isRoute = Boolean(item.route);
          const href = isRoute ? item.route : `#${item.id}`;
          return (
            <a
              key={item.id}
              role="menuitem"
              tabIndex={open ? 0 : -1}
              data-testid={`nav-dropdown-item-${link.id}-${item.id}`}
              href={href}
              onClick={(e) => {
                if (!isRoute) e.preventDefault();
                setOpen(false);
                onLinkClick(item);
              }}
              className="block px-5 py-2.5 text-[11px] tracking-[0.18em] uppercase text-themed-muted hover:text-gold hover:bg-gold/5 transition-colors"
            >
              {item.label}
            </a>
          );
        })}
      </div>
    </div>
  );
};

export default NavDropdown;
